export type SaveStatus = "idle" | "saving" | "saved" | "error";

/** Regroupe les frappes, sérialise les écritures et garde les corrections en cas d'échec. */
export function createPatchQueue<T extends object>(
  write: (patch: Partial<T>) => Promise<void>,
  notify: (status: SaveStatus) => void,
  delay = 500,
) {
  let pending: Partial<T> = {};
  let sending: Partial<T> = {};
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight: Promise<boolean> | null = null;
  const hasPending = () => Object.keys(pending).length > 0;
  async function flush(): Promise<boolean> {
    clearTimeout(timer);
    if (inFlight) {
      if (!(await inFlight)) return false;
      return flush();
    }
    if (!hasPending()) return true;
    const batch = pending;
    pending = {};
    sending = batch;
    notify("saving");
    inFlight = Promise.resolve()
      .then(() => write(batch))
      .then(
        () => {
          sending = {};
          notify(hasPending() ? "saving" : "saved");
          return true;
        },
        () => {
          pending = { ...batch, ...pending };
          sending = {};
          notify("error");
          return false;
        },
      )
      .finally(() => {
        inFlight = null;
      });
    if (!(await inFlight)) return false;
    return hasPending() ? flush() : true;
  }
  return {
    enqueue(patch: Partial<T>) {
      pending = { ...pending, ...patch };
      notify("saving");
      clearTimeout(timer);
      timer = setTimeout(() => {
        void flush();
      }, delay);
    },
    flush,
    unsaved: () => ({ ...sending, ...pending }),
  };
}
